import {BehaviorSubject} from 'rxjs';
import {stringify} from 'query-string';
import {sd} from '@px/da';
import {mc} from './mc';
import {guid} from './guid';
import {isLocalizedSite, defaultSiteByHost} from './localized-site';
import {genSystemDeviceForHttp} from './device';
import {envUtil} from './env';

interface HttpConfig {
  readToken: () => string;
  writeToken: (token: string) => void;
  removeToken: () => void;
  readBid: () => string;
  readLanguage: () => string;
}

const TOKEN_HEADER = 'phemex-auth-token';
const TOKEN_SITE = 'x-phemex-site';

const httpConfig: HttpConfig = {
  readToken: null,
  writeToken: null,
  removeToken: null,
  readBid: null,
  readLanguage: null,
};

export const http = {init, get, post, put, delete: del};

export const httpReady$ = new BehaviorSubject<boolean>(false);

function init(config: HttpConfig) {
  Object.assign(httpConfig, config);
  httpReady$.next(true);
}

function fillHeaders(xhr, headers) {
  xhr.setRequestHeader('bid', httpConfig.readBid());
  xhr.setRequestHeader('Accept-Language', httpConfig.readLanguage());
  xhr.setRequestHeader('x-phemex-request-tracing', guid.build());
  isLocalizedSite && xhr.setRequestHeader(TOKEN_SITE, defaultSiteByHost);
  if (envUtil.isApp) {
    xhr.setRequestHeader('x-phemex-device', genSystemDeviceForHttp());
  }
  const token = httpConfig.readToken();
  token && xhr.setRequestHeader(TOKEN_HEADER, token);
  headers && Object.keys(headers).forEach((key) => xhr.setRequestHeader(key, headers[key]));
}

function ajax(options) {
  options = options || {};
  const query = options.querystring ? '?' + options.querystring : options.query ? '?' + stringify(options.query) : '';
  const url = options.url + query;

  const xhr = window.XMLHttpRequest ? new XMLHttpRequest() : new ActiveXObject('Microsoft.XMLHTTP');
  xhr.onreadystatechange = () => {
    if (xhr.readyState === 2) {
      const token = xhr.getResponseHeader(TOKEN_HEADER);
      if (token) {
        httpConfig.writeToken(token);
        mc.send('http', 'token-change', {token});
      }
    }
    if (xhr.readyState === 4) {
      const status = xhr.status;
      if ((status >= 200 && status < 300) || status === 400) {
        const reply = JSON.parse(xhr.responseText);
        if (reply.code === 0) {
          if (options.success) {
            options.success(options.formatter ? options.formatter(reply.data) : reply.data, reply);
          }
          return;
        }

        if (options.fail) {
          options.fail({code: reply.code, message: reply.msg}, reply);
          collectHttpError(url, reply.code, xhr);
          return;
        }
      }

      if (status === 401) {
        const reply = JSON.parse(xhr.responseText);
        httpConfig.removeToken();
        mc.send('http', '401', {reply});
        collectHttpError(url, reply.code, xhr);
        return;
      }

      if (status === 403) {
        options.fail({code: 403, message: 'Request denied'}, null);
        collectHttpError(url, 403, xhr);
        return;
      }

      if (status === 429) {
        const message = 'Too many requests, Please try again later';
        options.fail({code: status, message}, null);
        mc.send('toast', 'error', {message});
        collectHttpError(url, status, xhr);
        return;
      }

      if (options.fail) {
        console.warn('network:', status);
        options.fail({code: 499, message: 'network error'}, null);
        collectHttpError(url, 499, xhr);
      }
    }
  };

  const body = options.params ? JSON.stringify(options.params) : null;

  xhr.open(options.type, url, true);
  fillHeaders(xhr, options.headers);
  xhr.send(body);
}

function get<T = any>(url, {query, ...rest}: HttpOptions<T>) {
  return new Promise<HttpResult<T>>((resolve) =>
    ajax({
      url,
      type: 'GET',
      query,
      success(data, reply) {
        resolve({data, reply});
      },
      fail(error, reply) {
        resolve({error, reply});
      },
      ...rest,
    })
  );
}

function post<T = any>(url, {query, params, ...rest}: HttpOptions<T>) {
  return new Promise<HttpResult<T>>((resolve) =>
    ajax({
      url,
      type: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      query,
      params,
      success(data, reply) {
        resolve({data, reply});
      },
      fail(error, reply) {
        resolve({error, reply});
      },
      ...rest,
    })
  );
}

function put<T = any>(url, {query, params, ...rest}: HttpOptions<T>) {
  return new Promise<HttpResult<T>>((resolve) =>
    ajax({
      url,
      type: 'PUT',
      headers: {
        'Content-Type': 'application/json',
      },
      query,
      params,
      success(data, reply) {
        resolve({data, reply});
      },
      fail(error, reply) {
        resolve({error, reply});
      },
      ...rest,
    })
  );
}

function del<T = any>(url, {query, params, ...rest}: HttpOptions<T>) {
  return new Promise<HttpResult<T>>((resolve) =>
    ajax({
      url,
      type: 'DELETE',
      query,
      params,
      success(data, reply) {
        resolve({data, reply});
      },
      fail(error, reply) {
        resolve({error, reply});
      },
      ...rest,
    })
  );
}

function collectHttpError(url, code, {status, responseText}) {
  // 注册未完成错误码忽略（轮询操作）
  if (code === 31108) {
    return;
  }

  sd.track('web_debug', {debug_type: 'http_error', debug_info: {url, status, responseText}});
}
